import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Users, TrendingUp, Globe, Heart } from 'lucide-react';
import { motion } from 'framer-motion';
import PageSeo from '@/components/PageSeo';

export default function CustomerStories() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { label: 'Active Travelers', value: '50,000+', icon: Users },
    { label: 'Countries Reached', value: '120+', icon: Globe },
    { label: 'Bookings Growth', value: '340%', icon: TrendingUp },
    { label: 'Customer Satisfaction', value: '4.9/5', icon: Heart }
  ];

  const stories = [
    {
      name: "Maria Rodriguez",
      occupation: "Retail Associate",
      location: "Phoenix, AZ",
      avatar: "👩‍💼",
      quote: "I've been saving to visit my family in Mexico for two years. With Defera's payment plan, I finally booked my ticket and paid it off over six months. No more waiting years to see my mom.",
      achievement: "First international trip in 5 years",
      stats: [
        { label: "Trip cost", value: "$420" },
        { label: "Monthly payment", value: "$70" },
        { label: "Months to pay off", value: "6" }
      ]
    },
    {
      name: "James Carter",
      occupation: "Warehouse Worker",
      location: "Columbus, OH",
      avatar: "👨‍🏭",
      quote: "My daughter's wedding was in California and I'm in Ohio. I thought I'd have to miss it because I couldn't afford the upfront cost. Defera let me split the payments and I made it to walk her down the aisle.",
      achievement: "Attended daughter's wedding",
      stats: [
        { label: "Trip cost", value: "$380" },
        { label: "Down payment", value: "$95" },
        { label: "Saved compared to credit card", value: "$85" }
      ]
    },
    {
      name: "Priya Patel",
      occupation: "Restaurant Server",
      location: "Houston, TX",
      avatar: "👩‍🍳",
      quote: "I work two jobs and tips are unpredictable. When my brother got sick back home in India, I needed to fly ASAP but didn't have $1,200 sitting around. Defera's instant approval with no credit check was a lifesaver.",
      achievement: "Emergency family visit within 24 hours",
      stats: [
        { label: "Trip booked in", value: "< 1 hour" },
        { label: "Down payment only", value: "$300" },
        { label: "No credit check", value: "✓" }
      ]
    },
    {
      name: "Marcus Johnson",
      occupation: "Delivery Driver",
      location: "Atlanta, GA",
      avatar: "🚗",
      quote: "I've been driving for DoorDash for three years, saving every penny to take my kids to Disney. Other booking sites wanted everything upfront. Defera let me pay $150 a month and my kids got their dream vacation.",
      achievement: "First family vacation in 8 years",
      stats: [
        { label: "Total trip cost", value: "$1,800" },
        { label: "Monthly payment", value: "$150" },
        { label: "Months to save", value: "12" }
      ]
    },
    {
      name: "Lisa Nguyen",
      occupation: "Home Health Aide",
      location: "Seattle, WA",
      avatar: "👩‍⚕️",
      quote: "I take care of elderly patients 60 hours a week. When my best friend invited me to her destination wedding in Hawaii, I thought there was no way. Defera made it possible - I'm going to my first beach vacation ever at 42.",
      achievement: "First vacation in adult life",
      stats: [
        { label: "Trip cost", value: "$680" },
        { label: "Initial payment", value: "$170" },
        { label: "Price locked months ago", value: "✓" }
      ]
    },
    {
      name: "David Gonzales",
      occupation: "Construction Worker",
      location: "Denver, CO",
      avatar: "👷",
      quote: "Construction work is seasonal. I needed to fly to Texas for a job opportunity but it was during my slow season. Defera's flexible payments meant I could book now and pay when work picked back up. Got the job!",
      achievement: "Secured new job opportunity",
      stats: [
        { label: "Flight cost", value: "$295" },
        { label: "Started paying", value: "2 months later" },
        { label: "New job salary increase", value: "$8K/year" }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white/80">
      <PageSeo path="/customers" />
      <div className="mx-auto max-w-6xl px-6 py-12">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-16">
          <h1 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-tight">
            Customer Stories
          </h1>
          <p className="text-base text-white/80 max-w-2xl leading-relaxed">
            Real people, real journeys. See how everyday travelers are using Defera to visit family, chase opportunities, and make memories without breaking the bank.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
              >
                <Icon className="w-6 h-6 text-[var(--accent-color)] mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-light text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-xs text-white/50">
                  {stat.label}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Customer Stories */}
        <div className="space-y-12">
          {stories.map((story, index) => (
            <motion.div
              key={story.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + index * 0.1 }}
              className="bg-white/5 border border-white/10 rounded-2xl p-8 md:p-10"
            >
              {/* Person Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="text-4xl">{story.avatar}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-light text-white mb-1">
                    {story.name}
                  </h3>
                  <p className="text-sm text-white/60">
                    {story.occupation} · {story.location}
                  </p>
                  <div className="mt-2 bg-[var(--accent-color)]/20 text-[var(--accent-color)] text-xs px-3 py-1 rounded-full inline-block">
                    {story.achievement}
                  </div>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="mb-6">
                <p className="text-base md:text-lg text-white/90 leading-relaxed font-light italic">
                  "{story.quote}"
                </p>
              </blockquote>

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t border-white/10">
                {story.stats.map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl font-light text-[var(--accent-color)] mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs text-white/50">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="mt-20 text-center bg-white/5 border border-white/10 rounded-2xl p-12"
        >
          <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
            Your journey starts here
          </h2>
          <p className="text-base text-white/80 mb-8 max-w-2xl mx-auto">
            Join thousands of everyday travelers making memories, visiting loved ones, and seizing opportunities - all with flexible payments that fit your life.
          </p>
          <Link
            to="/search"
            className="inline-flex items-center gap-2 bg-[var(--accent-color)] hover:bg-[var(--accent-color)]/90 text-white px-8 py-3 rounded-full font-normal transition-colors text-base"
          >
            Get Started Today
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
